import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Flame, Trophy, PlayCircle, MessageCircle, Loader2, Activity, Users, Image as ImageIcon } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { BirthdayWishes } from "@/components/BirthdayCard";
import { FbHeader, IntroCard, Card, PhotoGrid, usePhotos, FriendsPreview } from "@/components/profile/FbProfile";
import { SupporterProfileHeader } from "@/components/profile/SupporterProfileHeader";
import { ItzcoupleThanks } from "@/components/profile/SupporterThanks";
import { SupporterWelcome } from "@/components/profile/SupporterWelcome";
import { ProfileWall } from "@/components/ProfileWall";
import { ProfilePet } from "@/components/ProfilePet";
import { SupporterCongrats } from "@/components/SupporterCongrats";
import { MysteryCountdownBadge } from "@/components/MysteryCountdownBadge";
import { PageExplain } from "@/components/PageExplain";
import { PostComposer } from "@/components/PostComposer";
import { PostList, usePosts } from "@/components/PostList";
import { ModerationPanel } from "@/components/Moderation";
import { Badges } from "@/components/Badges";
import { MemberGrid } from "@/components/MemberCard";
import { useAuth } from "@/context/AuthContext";
import { TikTokLiveToggle } from "@/components/TikTokLive";
import { AdminNudgeButtons } from "@/components/admin/AdminNudgeButtons";
import { HonorPromote } from "@/components/profile/HonorPromote";
import { ProfileProgress } from "@/components/ProfileProgress";

const PROFILE_STEPS = [
  "Das ist das Profil eines Community-Mitglieds – E-Mail-Adressen sind hier nie sichtbar.",
  "Mit „Freund hinzufügen“ schickst du eine Freundschaftsanfrage. Sobald sie angenommen ist, erscheint ihr gegenseitig im Reiter „Freunde“.",
  "Unter „Beiträge“ siehst du alles, was das Mitglied im Feed gepostet hat – du kannst liken und kommentieren.",
  "Abzeichen gibt's automatisch für Login-Serien, aktive Tage, eingeladene Freunde, Geschenke, Top-3-Platzierungen, beliebte Beiträge mit vielen Reaktionen (10 = „Publikumsliebling“, 50 = „Community-Star“) und als „Early Bird“. Eigene Abzeichen kannst du als Bild teilen.",
];

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="bg-rose-50/60 rounded-2xl p-4 flex items-center gap-3" data-testid={testId}>
    <Icon className="w-5 h-5 text-rose-500" /><div><p className="font-display text-xl font-extrabold leading-none">{value}</p><p className="text-xs text-slate-500 mt-1">{label}</p></div>
  </div>
);

const ActivityGrid = ({ p }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="profile-activity">
    <Stat icon={Flame} label="Tage-Serie" value={p.streak} testId="public-stat-streak" />
    <Stat icon={PlayCircle} label="Beiträge" value={p.posts_count ?? 0} testId="public-stat-posts" />
    <Stat icon={MessageCircle} label="Kommentare" value={p.comments} testId="public-stat-comments" />
    <Stat icon={Trophy} label="Rang gesamt" value={p.rank ? `#${p.rank}` : "–"} testId="public-stat-rank-activity" />
  </div>
);

function FriendsTab({ userId, kind }) {
  const [list, setList] = useState(null);
  useEffect(() => { setList(null); api.get(`/users/${userId}/${kind}`).then(({ data }) => setList(data)); }, [userId, kind]);
  return !list ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-rose-500" /></div>
    : <MemberGrid list={list} testId="profile-friends-list" empty={kind === "following" ? "Folgt noch niemandem." : kind === "followers" ? "Noch keine Follower." : "Noch keine Freunde."} />;
}

function PostsTab({ p, photos, goTab }) {
  const feed = usePosts(`/users/${p.user_id}/posts`);
  return (
    <div className="grid lg:grid-cols-[2fr_3fr] gap-5 items-start">
      <div className="space-y-5 lg:sticky lg:top-24">
        <IntroCard p={p} />
        <Card title="Fotos" icon={ImageIcon} testId="profile-photos-card" action={photos?.length > 0 && <button onClick={() => goTab("photos")} className="text-sm font-semibold text-rose-600 hover:underline">Alle Fotos</button>}>
          <PhotoGrid photos={photos} limit={9} />
        </Card>
        <FriendsPreview userId={p.user_id} count={p.friends} onAll={() => goTab("friends")} />
        <MysteryCountdownBadge userId={p.user_id} isMe={p.is_me} name={p.name} />
        <SupporterCongrats userId={p.user_id} isMe={p.is_me} />
        <ProfilePet p={p} />
        <ProfileWall p={p} />
        <Badges userId={p.user_id} isMe={p.is_me} />
      </div>
      <div className="space-y-4 min-w-0">
        {p.is_me && <PostComposer onPosted={feed.prepend} />}
        <PostList feed={feed} empty={p.is_me ? "Du hast noch nichts gepostet." : "Noch keine Beiträge."} />
      </div>
    </div>
  );
}

const TABS = [["posts", "Beiträge"], ["about", "Info"], ["friends", "Freunde"], ["photos", "Fotos"], ["followers", "Follower"], ["following", "Folgt"], ["activity", "Aktivität"]];

function ProfileBody({ p, tab, setTab }) {
  const photos = usePhotos(p.user_id);
  const views = {
    posts: () => <PostsTab p={p} photos={photos} goTab={setTab} />,
    about: () => <div className="max-w-2xl"><IntroCard p={p} /></div>,
    followers: () => <Card title="Follower" icon={Users}><FriendsTab userId={p.user_id} kind="followers" /></Card>,
    following: () => <Card title="Folgt" icon={Users}><FriendsTab userId={p.user_id} kind="following" /></Card>,
    friends: () => <Card title="Freunde" icon={Users}><FriendsTab userId={p.user_id} kind="friends" /></Card>,
    photos: () => <Card title="Fotos" icon={ImageIcon}><PhotoGrid photos={photos} limit={60} testId="profile-photos-all" /></Card>,
    activity: () => <Card title="Aktivität" icon={Activity}><ActivityGrid p={p} /></Card>,
  };
  return (
    <div className="space-y-5">
      <nav className={`flex gap-1 overflow-x-auto no-scrollbar -mx-4 sm:mx-0 scroll-mt-28 [mask-image:linear-gradient(to_right,black_85%,transparent)] sm:[mask-image:none] ${(p.is_supporter && !p.is_honor) ? "sp-tabs rounded-2xl px-1.5" : "bg-white rounded-2xl px-1.5 border border-pink-100"}`} data-testid="profile-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`profile-tab-${k}`}
            className={(p.is_supporter && !p.is_honor)
              ? `relative shrink-0 px-3 sm:px-5 h-12 text-sm font-bold transition-colors ${tab === k ? "text-[#9d1446]" : "text-[#9a5470]/80 hover:text-[#9d1446]"}`
              : `relative shrink-0 px-3 sm:px-4 h-12 text-sm font-bold transition-colors ${tab === k ? "text-rose-600" : "text-slate-500 hover:text-slate-800"}`}>
            {l}{tab === k && <span className={`absolute left-3 right-3 h-[3px] rounded-full ${(p.is_supporter && !p.is_honor) ? "bg-gradient-to-r from-rose-400 via-amber-400 to-rose-400 bottom-1.5" : "bg-rose-500 bottom-0"}`} />}
          </button>
        ))}
      </nav>
      <div>{views[tab]()}</div>
    </div>
  );
}

export default function PublicProfile() {
  const { userId } = useParams();
  const { user } = useAuth();
  const staff = ["admin", "moderator"].includes(user?.role);
  const [p, setP] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("posts");
  const load = useCallback(() => api.get(`/users/${userId}/profile`).then(({ data }) => setP(data)).catch((e) => setError(formatErr(e))), [userId]);
  useEffect(() => { setP(null); setError(""); setTab("posts"); load(); }, [load]);

  const vipPage = Boolean(p?.is_supporter) && !p?.is_honor;
  useEffect(() => {
    document.body.classList.toggle("rose-page", vipPage);
    document.body.classList.toggle("honor-page", Boolean(p?.is_honor));
    return () => document.body.classList.remove("rose-page", "honor-page");
  }, [vipPage, p?.is_honor]);
  if (error) return <p className="text-center text-slate-500 py-20" data-testid="public-profile-error">{error}</p>;
  if (!p) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>;

  return (
    <div className={`space-y-5 max-w-5xl mx-auto -mt-8 sm:mt-0 ${vipPage ? "rose-scope" : p.is_honor ? `honor-scope tier-${p.honor_tier || "gold"}` : ""}`} data-vip-page={vipPage || undefined}>
      {vipPage ? <SupporterProfileHeader p={p} onChanged={load} /> : <FbHeader p={p} onChanged={load} onTab={setTab} />}
      {vipPage && <ItzcoupleThanks key={`thanks-${p.user_id}`} p={p} />}
      {vipPage && p.is_me && <SupporterWelcome key={`welcome-${p.user_id}`} p={p} />}
      {user?.role === "admin" && <div className="flex justify-center sm:justify-start px-4 sm:px-0"><HonorPromote p={p} onDone={load} /></div>}
      {user?.role === "admin" && p.is_me && <div className="flex flex-wrap justify-center sm:justify-start gap-2 px-4 sm:px-0" data-testid="tiktok-live-admin"><TikTokLiveToggle /><AdminNudgeButtons /></div>}
      {p.is_me && <ProfileProgress hideWhenDone linkToEdit />}
      <BirthdayWishes userId={p.user_id} isMe={p.is_me} />
      {staff && <ModerationPanel profile={p} onChanged={load} />}
      <ProfileBody key={p.user_id} p={p} tab={tab} setTab={setTab} />
      <PageExplain testId="public-profile-explain" steps={PROFILE_STEPS} />
    </div>
  );
}
